import * as React from "react";

import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

import logger from "../log";

interface ToastContextType {
    showSuccess: (message: string) => void;
    showError: (message: string) => void;
    showInfo: (message: string) => void;
    showWarning: (message: string) => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(
    undefined,
);

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState("");
    const [severity, setSeverity] = React.useState<
        "success" | "error" | "info" | "warning"
    >("info");

    const showToast = (
        msg: string,
        sev: "success" | "error" | "info" | "warning",
    ) => {
        setMessage(msg);
        setSeverity(sev);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const value = React.useMemo(
        () => ({
            showSuccess: (msg: string) => showToast(msg, "success"),
            showError: (msg: string) => showToast(msg, "error"),
            showInfo: (msg: string) => showToast(msg, "info"),
            showWarning: (msg: string) => showToast(msg, "warning"),
        }),
        [],
    );

    return (
        <ToastContext.Provider value={value}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={6000}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleClose}
                    severity={severity}
                    sx={{ width: "100%" }}
                >
                    {message}
                </Alert>
            </Snackbar>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const context = React.useContext(ToastContext);

    // Memoize fallback functions to prevent recreation on every render
    const fallbackFunctions = React.useMemo(
        () => ({
            showSuccess: (message: string) =>
                logger.info(`Success: ${message}`),
            showError: (message: string) => logger.error(`Error: ${message}`),
            showInfo: (message: string) => logger.info(`Info: ${message}`),
            showWarning: (message: string) =>
                logger.warn(`Warning: ${message}`),
        }),
        [],
    );

    if (!context) {
        // Return stable fallback functions if context is not available
        return fallbackFunctions;
    }
    return context;
}
